/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-use-before-define */
import useCommon from '@utils/hooks/common';
import {Button, Modal, notification} from 'antd';
import React, {useEffect, useRef, useImperativeHandle, useState, useMemo, useCallback} from 'react';
import {useRouter} from 'next/router';
import styles from './styles';
import GameRoom from '@components/game-room/GameRoom';
import {getGameEventV3} from '@constant/game';
import {useGameMode} from '@utils/hooks/gameSettings';
import {GameRoomComponent} from '@interface';
import {
  ErrorCodes,
  GameTableResponse,
  JoinedRoomProps,
} from 'poker-interfaces/game-room/game-room-interface';
import {showSystemError} from '@utils/index';
import {autorun} from 'mobx';
import {useWhyDidYouUpdate} from 'ahooks';
import CustomPopup from '@components/CustomPopup';
import {useDebugView} from '@utils/hooks/debug';
import {get} from 'lodash-es';
import {isTelegramMiniApp} from '@components/layout';

export const TABLE_MESSAGE_DURATION_MS = 3000;

interface TablesInterface {
  tablesInfo: Array<JoinedRoomProps>;
  encryptionKeys: Array<string>;
  tablesOnCopy?: Function;
  tablesOnShare?: Function;
  tablesOnClickEmoji?: Function;
  tablesOnClickHistory?: Function;
  tablesOnClickWithdrawExtraBet?: Function;
  tablesOnClickAddExtraBet?: Function;
  quitTable?: (roomCode: string) => Promise<any>;
  sitOut?: (roomCode: string) => Promise<any>;
  sitIn?: (roomCode: string) => Promise<any>;
  sitOutPopUp?: (roomCode: string) => Promise<any>;
  setIsWithdrawChipsBtnShown?: Function;
  setIsDepositChipsBtnShown?: Function;
  setIsEmojiBtnShown?: Function;
  setIsSitOutNextHand?: (roomIndex: number, state: boolean) => void;
  areTablesMuted: boolean;
  onAudioError: () => void;
  setObserverStatus?: Function;
}

const Tables = React.memo(
  React.forwardRef((props: TablesInterface, ref) => {
    const {classes, socket, t, system, user} = useCommon({
      withStyles: styles,
      observers: ['socket', 'user', 'system', 'user'],
      withTranslation: ['errorCodes', 'deposit_withdraw'],
    });

    const table0Ref = useRef<GameRoomComponent>();
    const table1Ref = useRef<GameRoomComponent>();
    const table2Ref = useRef<GameRoomComponent>();
    const table3Ref = useRef<GameRoomComponent>();

    const tablesRefArray = useMemo(
      () => [table0Ref, table1Ref, table2Ref, table3Ref],
      [table0Ref, table1Ref, table2Ref, table3Ref]
    );

    // useWhyDidYouUpdate('Tables', {...props});

    const {
      tablesInfo,
      encryptionKeys,
      setIsSitOutNextHand,
      setIsWithdrawChipsBtnShown,
      setIsDepositChipsBtnShown,
      setIsEmojiBtnShown,
      areTablesMuted,
      onAudioError,
      setObserverStatus,
    } = props;
    const GAME_EVENTS_V3 = getGameEventV3();
    const router = useRouter();
    const isDebugView = useDebugView();

    const [socketClient, setSocketClient] = useState(null);
    const [activeTableIndex, setActiveTableIndex] = useState<number>(0);
    const [isGameOverlayShown, setIsGameOverlayShown] = useState<boolean>(false);
    const [isDepositPopupVisible, setIsDepositPopupVisible] = useState<boolean>(false);
    const [isTournamentEndedPopupVisible, setIsTournamentEndedPopupVisible] =
      useState<boolean>(false);
    // Tournament cancel popup moved to lobby level
    // const [isTournamentCanceledPopupVisible, setIsTournamentCanceledPopupVisible] =
    //   useState<boolean>(false);
    const [joinRoomErrorMessage, setJoinRoomErrorMessage] = useState<string>(null);

    autorun(() => {
      if (socket?.client !== socketClient) {
        setSocketClient(socket?.client);
      }
      if (activeTableIndex !== system?.activeTableIndex) {
        setActiveTableIndex(system.activeTableIndex);
      }
      if (isGameOverlayShown !== system?.isGameOverlayShown) {
        setIsGameOverlayShown(system.isGameOverlayShown);
      }
    });

    useImperativeHandle(
      ref,
      () => ({
        tablesOnCopy,
        tablesOnShare,
        tablesOnClickEmoji,
        tablesOnClickHistory,
        tablesOnClickWithdrawExtraBet,
        tablesOnClickAddExtraBet,
        disconnectTable,
        quitTable,
        sitOut,
        sitIn,
        sitOutPopUp,
        unlockAudio,
      }),
      [tablesInfo]
    );

    useEffect(() => {
      /** https://nextjs.org/docs/api-reference/next/router#routerbeforepopstate */
      router.beforePopState(({as}) => {
        if (as !== router.asPath) {
          // Will run when leaving the current page; on back/forward actions
          tablesRefArray?.forEach((tableRef, index) => {
            if (tablesInfo[index]?.roomCode) {
              tableRef?.current?.disconnectTable();
            }
          });
        }
        return true;
      });

      return () => {
        router.beforePopState(() => true);
      };
    }, [router, tablesInfo]);

    useEffect(() => {
      if (socketClient) {
        socketClient.socket.on(GAME_EVENTS_V3.DIRECT_GAME_INVITE, data =>
          handlerWrapper(data, directGameInviteHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.DIRECT_SET_BASE, data =>
          handlerWrapper(data, buyInAgainHandler)
        );

        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_GENERAL, data =>
          handlerWrapper(data, roomInitHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_GAME_INIT, data =>
          handlerWrapper(data, gameInitHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_PLAYER_RANKING, data =>
          handlerWrapper(data, tournamentPlayerRankingHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_PLAYER_IN_ACTION, data =>
          handlerWrapper(data, playerInActionHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_TABLE, data =>
          handlerWrapper(data, tableUpdateHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_GAME_RESULT, data =>
          handlerWrapper(data, gameResultHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_GAME_RESTART, data =>
          handlerWrapper(data, gameRestartHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_STICKER_SUCCESS, data =>
          handlerWrapper(data, stickerHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.BROADCAST_INSURANCE, data =>
          handlerWrapper(data, insuranceHandler)
        );

        socketClient.socket.on(GAME_EVENTS_V3.EM_SERVER_PRIVATE_INFO, data =>
          handlerWrapper(data, privateInfoHandler)
        );
        socketClient.socket.on(GAME_EVENTS_V3.EM_SERVER_YOUR_TURN, data =>
          handlerWrapper(data, yourTurnHandler)
        );
        socketClient.socket.on(
          GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_AFK_LEAVE_TABLE,
          data => {
            handlerWrapper(data, afkNotification);
          }
        );

        socketClient.socket.on(GAME_EVENTS_V3.JOIN_ROOM_RESULT, data => {
          handlerWrapper(data, handleJoinRoomResult);
        });

        socketClient.socket.on(GAME_EVENTS_V3.JOIN_TABLE_RESULT, data => {
          handlerWrapper(data, handleJoinTableResult);
        });

        socketClient.socket.on(
          GAME_EVENTS_V3.BROADCAST_NOTIFICATIONS_PRIVATE_ROOM_TIME_EXPIRED,
          data => {
            handlerWrapper(data, privateRoomExpiredNotification);
          }
        );
        socketClient.socket.on(
          GAME_EVENTS_V3.BROADCAST_NOTIFICATIONS_PRIVATE_ROOM_TIME_REMAINING,
          data => {
            handlerWrapper(data, privateRoomRemainingNotification);
          }
        );

        socketClient.socket.on(
          GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_FORCE_EXIT_ROOM,
          data => {
            handlerWrapper(data, exitRoomNotification);
          }
        );

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM_CONNECTED, () => {
          reconnectTable();
        });

        socketClient.socket.on(
          GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_SET_BUY_IN_EXPIRED,
          data => {
            console.log('buy in expired received');
            handlerWrapper(data, buyInExpired);
          }
        );

        socketClient.socket.on(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_SIT_OUT, data => {
          handlerWrapper(data, sitOutPopUp);
        });

        socketClient.socket.on(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_SIT_OUT_KICK, data => {
          handlerWrapper(data, sitOutKick);
        });

        socketClient.socket.on(GAME_EVENTS_V3.SET_BUY_IN_RESULT, data => {
          handlerWrapper(data, onBuyIn);
        });

        socketClient.socket.on(GAME_EVENTS_V3.SET_AUTO_ACTION_RESULT, data => {
          handlerWrapper(data, onAutoAction);
        });

        socketClient.socket.on(GAME_EVENTS_V3.SWITCH_TO_OBSERVER_RESULT, data => {
          handlerWrapper(data, onSwitchToObserverResp);
        });

        socketClient.socket.on(GAME_EVENTS_V3.ADD_EXTRA_BET_RESULT, data => {
          handlerWrapper(data, onAddExtraBetResult);
        });

        socketClient.socket.on(GAME_EVENTS_V3.SET_ACTION_RESULT, data => {
          // * move to EM_PLAYER_SET_ACTION_RESULT coz that is before broadcast/table
          // handlerWrapper(data, onSetActionResult);
        });

        // socketClient.socket.on(GAME_EVENTS_V3.WITHDRAW_EXTRA_BET_RESULT, data => {
        //   handlerWrapper(data, onWithdrawBetResult);
        // })

        socketClient.socket.on(
          GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_NO_ENOUGH_MONEY_TO_BUY_IN,
          () => {
            // notification.info({message: t('HP_0003')});
            setIsDepositPopupVisible(true);
          }
        );

        socketClient.socket.on(
          GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_WALLET_TOP_UP_REQUIRED,
          () => {
            // notification.info({message: t('HP_0003')});
            setIsDepositPopupVisible(true);
          }
        );

        socketClient.socket.on(GAME_EVENTS_V3.EM_PLAYER_SET_ACTION_RESULT, data => {
          // * to achieve hidden action buttons before chips are from players to pot
          handlerWrapper(data, onSetActionResult);
        });

        socketClient.socket.on(GAME_EVENTS_V3.SIT_IN_RESULT, data => {
          handlerWrapper(data, onSitInResult);
        });

        socketClient.socket.on(GAME_EVENTS_V3.SIT_OUT_RESULT, data => {
          handlerWrapper(data, onSitOutResult);
        });

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_RESULT_ANNOUNCEMENT, data => {
          handlerWrapper(data, tournamentResultAnnouncementHandler);
        });

        socketClient.socket.on(GAME_EVENTS_V3.TOURNAMENT.TOURNAMENT_STACK, data => {
          handlerWrapper(data, tournamentStackHandler);
        });

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_REBUY_INVITE, data => {
          handlerWrapper(data, tournamentRebuyInviteHandler);
        });

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_THANKS_FOR_JOINING, data => {
          handlerWrapper(data, tournamentThanksForJoiningHandler);
        });

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_CANCELLED, data => {
          console.log('TOURNAMENT_CANCELLED raw event data:', data);
          handlerWrapper(data, tournamentCancelHandler);
        });

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_WAITING_FOR_PLAYERS, data => {
          handlerWrapper(data, tournamentWaitingForPlayersHandler);
        });

        socketClient.socket.on(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_WAITING_STATUS_UPDATE, data => {
          handlerWrapper(data, tournamentWaitingStatusUpdateHandler);
        });
      }

      return () => {
        if (socketClient) {
          socketClient.socket.off(GAME_EVENTS_V3.DIRECT_GAME_INVITE);
          socketClient.socket.off(GAME_EVENTS_V3.DIRECT_SET_BASE);

          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_GENERAL);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_GAME_INIT);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_PLAYER_RANKING);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_PLAYER_IN_ACTION);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_TABLE);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_GAME_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_GAME_RESTART);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_STICKER_SUCCESS);
          socketClient.socket.off(GAME_EVENTS_V3.BROADCAST_INSURANCE);

          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_PRIVATE_INFO);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_YOUR_TURN);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_BUY_INSURANCE);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_AFK_LEAVE_TABLE);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_FORCE_EXIT_ROOM);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_SIT_OUT);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_SIT_OUT_KICK);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM_CONNECTED);
          socketClient.socket.off(GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_SET_BUY_IN_EXPIRED);
          socketClient.socket.off(
            GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_NO_ENOUGH_MONEY_TO_BUY_IN
          );
          socketClient.socket.off(
            GAME_EVENTS_V3.EM_SERVER_NOTIFICATIONS_NOTIFY_WALLET_TOP_UP_REQUIRED
          );
          socketClient.socket.off(GAME_EVENTS_V3.SET_BUY_IN_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.SWITCH_TO_OBSERVER_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.ADD_EXTRA_BET_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.JOIN_ROOM_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.JOIN_TABLE_RESULT);
          // socketClient.socket.off(GAME_EVENTS_V3.WITHDRAW_EXTRA_BET_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.EM_PLAYER_SET_ACTION_RESULT);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_RESULT_ANNOUNCEMENT);
          socketClient.socket.off(GAME_EVENTS_V3.TOURNAMENT.TOURNAMENT_STACK);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_REBUY_INVITE);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_THANKS_FOR_JOINING);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_CANCELLED);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_WAITING_FOR_PLAYERS);
          socketClient.socket.off(GAME_EVENTS_V3.GAME_ROOM.TOURNAMENT_WAITING_STATUS_UPDATE);
        }
      };
    }, [tablesInfo, encryptionKeys, socket, socketClient]);

    const getMyPlayerId = () => get(user, 'playerInfo.id', null);

    const getRoomIndexByRoomNumber = (roomNumber: string): number => {
      return tablesInfo.findIndex(info => info.roomCode === roomNumber);
    };

    const handlerWrapper = (data, fn: Function, isEncrypted: boolean = false): Function => {
      let roomIndex: number;
      let broadcastData: any;
      if (!data) {
        roomIndex = null;
        broadcastData = null;
      } else if (!isEncrypted) {
        roomIndex = getRoomIndexByRoomNumber(data.roomCode);
        broadcastData = data?.data || data;
      } else {
        roomIndex = getRoomIndexByRoomNumber(data.roomCode);
        if (!data?.data) {
          broadcastData = null;
        } else if (encryptionKeys[roomIndex]) {
          broadcastData = null;
        } else {
          broadcastData = null;
        }
      }
      // console.log('tablesRef, roomIndex', broadcastData, roomNumbers, roomIndex);
      return fn(broadcastData, roomIndex);
    };

    const directGameInviteHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.directGameInviteHandler(data);
    };

    const buyInAgainHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.buyInAgainHandler(data);
    };

    const roomInitHandler = (data, roomIndex) => {
      const amIObserver = data.observerList?.some(
        observer => observer.playerId === getMyPlayerId()
      );

      setObserverStatus(prev => {
        if (prev[roomIndex] !== amIObserver) {
          const newStatus = [...prev];
          newStatus[roomIndex] = amIObserver; // update only if the value is different
          return newStatus;
        }
        return prev; // return previous state if there's no change
      });

      tablesRefArray?.[roomIndex]?.current?.roomInitHandler(data);
    };

    const tournamentPlayerRankingHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.tournamentPlayerRankingHandler(data);
    };

    const gameInitHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameInitHandler(data);
    };

    const playerInActionHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.playerInActionHandler(data);
    };

    const tableUpdateHandler = (data: GameTableResponse, roomIndex) => {
      const amIObserver = !data.players?.some(player => player?.id === getMyPlayerId());

      setObserverStatus(prev => {
        if (prev[roomIndex] !== amIObserver) {
          const newStatus = [...prev];
          newStatus[roomIndex] = amIObserver;
          return newStatus;
        }
        return prev;
      });

      const myPlayer = data?.players?.find(player => player && player.id === getMyPlayerId());
      if (myPlayer) {
        // console.log(
        //   '@@ Tables - tableUpdateHandler - roomCode - isSitOutNextHand, roomIndex',
        //   data.roomCode,
        //   myPlayer.isSitOutNextHand,
        //   roomIndex
        // );
        setIsSitOutNextHand(
          roomIndex,
          myPlayer.isSitOutNextHand || myPlayer.isSatOut || myPlayer.isTournamentSatOut
        );
        // setIsSitOutNextHand(roomIndex, myPlayer.isSitOutNextHand);
      }
      tablesRefArray?.[roomIndex]?.current?.tableUpdateHandler(data);
    };

    const gameResultHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameResultHandler({
        result: data.result,
        players: data.players,
        table: data.table,
      });
    };

    const gameRestartHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameResultHandler(data);
    };

    const stickerHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.stickerHandler(data);
    };

    const privateInfoHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.privateInfoHandler(data);
    };

    const yourTurnHandler = (data, roomIndex) => {
      // eslint-disable-next-line no-console
      console.log('%c🎯 [Tables] yourTurn received', 'background:#ff0;color:#000;padding:2px 6px;font-weight:bold', {
        roomIndex,
        refExists: !!tablesRefArray?.[roomIndex]?.current,
        hasHandler: typeof tablesRefArray?.[roomIndex]?.current?.yourTurnHandler === 'function',
        dataGameId: data?.gameId,
        availableActionsLen: data?.availableActions?.length,
      });
      tablesRefArray?.[roomIndex]?.current?.yourTurnHandler(data);
    };

    const insuranceHandler = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.insuranceHandler(data);
    };

    const afkNotification = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnAfkNotification(data);
    };

    const privateRoomExpiredNotification = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnExpired(data);
    };

    const privateRoomRemainingNotification = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnRemaining(data);
    };

    const exitRoomNotification = (data, roomIndex) => {
      console.log(' @@ exit room notification', data);
      
      // Handle nested data structure - data can be either at root level or nested in data.data
      const eventData = data?.data || data;
      const reason = eventData?.reason;
      const tournamentId = eventData?.tournamentId;
      const redirectTo = eventData?.redirectTo;
      
      console.log('Exit room data:', { reason, tournamentId, redirectTo });
      
      // Check if this is a tournament exit (has tournamentId and redirectTo)
      if (reason === 'tournament_ended' || (tournamentId && redirectTo === '/tournament')) {
        console.log('Tournament ended - preparing redirect');
        // If redirectTo is provided, use it
        if (redirectTo) {
          // Handle both absolute paths and paths with query params
          if (redirectTo === '/tournament' && tournamentId) {
            console.log('Redirecting to tournament page with:', {
              pathname: redirectTo,
              tournamentId,
              showResult: true,
              openPrizeTab: true
            });
            router.push({
              pathname: redirectTo,
              query: {
                tournamentId: String(tournamentId),
                showResult: 'true',
                openPrizeTab: 'true', // Add flag to open prize tab
              },
            });
          } else {
            router.push(redirectTo);
          }
          system?.hideGameOverlay();
        } else if (tournamentId) {
          // Fallback to legacy behavior
          console.log('Fallback redirect to tournament page with:', {
            tournamentId,
            showResult: true,
            openPrizeTab: true
          });
          router.push({
            pathname: '/tournament',
            query: {
              tournamentId: String(tournamentId),
              showResult: 'true',
              openPrizeTab: 'true', // Add flag to open prize tab
            },
          });
          system?.hideGameOverlay();
        }
      } else if (reason === 'tournament_canceled') {
        // Tournament cancel is now handled at table level in GameRoom
        console.log('Tournament canceled via force exit');
        
        // If redirectTo is provided for cancelled tournaments
        if (redirectTo) {
          router.push(redirectTo);
          system?.hideGameOverlay();
        }
      } else {
        // For any other reason, check if redirectTo is provided
        if (redirectTo) {
          router.push(redirectTo);
          system?.hideGameOverlay();
        }
      }
      
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnExitRoomNotification(data);
      user?.refreshJoinedRoom();
    };

    const tablesOnCopy = (roomNumber: string) => {
      tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.gameRoomComponentOnCopy(
        roomNumber
      );
    };

    const tablesOnShare = (roomNumber: string) => {
      tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.gameRoomComponentOnShare(
        roomNumber
      );
    };

    const tablesOnClickEmoji = (roomNumber: string) => {
      tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.gameRoomComponentOnClickEmoji(
        roomNumber
      );
    };

    const tablesOnClickHistory = (roomNumber: string) => {
      tablesRefArray?.[
        getRoomIndexByRoomNumber(roomNumber)
      ].current?.gameRoomComponentOnClickHistory(roomNumber);
    };

    const tablesOnClickWithdrawExtraBet = (roomNumber: string) => {
      tablesRefArray?.[
        getRoomIndexByRoomNumber(roomNumber)
      ]?.current?.gameRoomComponentOnClickWithdrawExtraBet(roomNumber);
    };

    const tablesOnClickAddExtraBet = (roomNumber: string) => {
      tablesRefArray?.[
        getRoomIndexByRoomNumber(roomNumber)
      ].current?.gameRoomComponentOnClickAddExtraBet(roomNumber);
    };

    const onBuyIn = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnBuyInResp(data);
    };

    const onAutoAction = (data, roomIndex) => {
      // TODO wait for BE to pass roomCode and privateInfo
      // tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnBuyInResp(data);
    };

    const onAddExtraBetResult = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnAddExtraBetSuccess(data);
      // TODO why roomIndex but not roomCode
      // tablesRef.current[
      //   getRoomIndexByRoomNumber(roomNumber)
      // ]?.gameRoomComponentOnAddExtraBetSuccess(data);
    };

    const onSetActionResult = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnSetActionResult(data);
    };

    // * sit out would also receive broadcast/table, but sit in may not have it
    const onSitInResult = (data, roomIndex) => {
      if (data?.success && roomIndex >= 0) {
        setIsSitOutNextHand(roomIndex, false);
      }
      // tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnSitInResult(data);
    };

    const onSitOutResult = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnSitOutResult(data);
    };

    const onSwitchToObserverResp = (data, roomIndex) => {
      tablesRefArray?.[roomIndex]?.current?.gameRoomComponentOnSwitchToObserverResp(data);
    };

    const disconnectTable = (roomNumber: string): Promise<any> => {
      console.log('roomNumber disconnect', roomNumber, getRoomIndexByRoomNumber(roomNumber));
      return tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.disconnectTable();
    };

    const quitTable = (roomNumber: string): Promise<any> => {
      console.log('roomNumber quit', roomNumber, getRoomIndexByRoomNumber(roomNumber));
      return tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.quitTable();
    };

    const sitOut = (roomNumber: string): Promise<any> => {
      return tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.sitOut();
    };

    const sitIn = (roomNumber: string): Promise<any> => {
      return tablesRefArray?.[getRoomIndexByRoomNumber(roomNumber)].current?.sitIn();
    };

    const unlockAudio = () => {
      tablesRefArray?.forEach(ref => ref?.current?.unlockAudio());
    };

    const buyInExpired = (data, roomIndex): void => {
      console.log('roomNumber', roomIndex);
      tablesRefArray?.[roomIndex]?.current?.buyInExpiredHandler();
    };

    const sitOutPopUp = (data, roomIndex: string): Promise<any> => {
      console.log('sit out popup data', data, roomIndex);
      return tablesRefArray?.[roomIndex]?.current?.sitOutPopUp(data);
    };

    const sitOutKick = (_data, roomIndex): void => {
      console.log('sit out kick', roomIndex);
      tablesRefArray?.[roomIndex]?.current?.sitOutKick();
    };

    const tournamentResultAnnouncementHandler = (data, roomIndex) => {
      console.log('Tournament result announcement received:', data);
      tablesRefArray?.[roomIndex]?.current?.tournamentResultAnnouncementHandler?.(data);
    };

    const tournamentRebuyInviteHandler = (data, roomIndex) => {
      console.log('Tournament rebuy invite received:', data);
      tablesRefArray?.[roomIndex]?.current?.tournamentRebuyInviteHandler?.(data);
    };

    const tournamentStackHandler = (data, roomIndex) => {
      console.log('Tournament stack update received:', data);
      tablesRefArray?.[roomIndex]?.current?.tournamentStackHandler?.(data);
    };

    const tournamentThanksForJoiningHandler = (data, roomIndex) => {
      console.log('Tournament thanks for joining received:', data);
      tablesRefArray?.[roomIndex]?.current?.tournamentThanksForJoiningHandler?.(data);
    };

    const tournamentCancelHandler = (data, roomIndex) => {
      console.log('Tournament cancel received:', data, 'roomIndex:', roomIndex);
      console.log('tablesRefArray:', tablesRefArray);
      console.log('tablesInfo:', tablesInfo);
      
      // If roomIndex is invalid, try to find tournament tables by tournamentId
      if (roomIndex < 0 || roomIndex === null) {
        console.log('Invalid roomIndex, checking all tables for tournament');
        
        // Try to find any tournament table
        tablesInfo.forEach((table, index) => {
          console.log(`Checking table ${index}:`, table);
          if (table?.roomType === 'TOURNAMENT') {
            console.log(`Found tournament table at index ${index}, calling handler`);
            if (tablesRefArray?.[index]?.current?.tournamentCancelHandler) {
              tablesRefArray[index].current.tournamentCancelHandler(data);
            }
          }
        });
      } else if (tablesRefArray?.[roomIndex]?.current?.tournamentCancelHandler) {
        console.log('Calling tournamentCancelHandler on GameRoom at roomIndex:', roomIndex);
        tablesRefArray[roomIndex].current.tournamentCancelHandler(data);
      } else {
        console.log('Could not call tournamentCancelHandler - invalid ref at roomIndex:', roomIndex);
      }
    };

    const tournamentWaitingForPlayersHandler = (data, roomIndex) => {
      console.log('Tournament waiting for players received:', data);
      tablesRefArray?.[roomIndex]?.current?.tournamentWaitingForPlayersHandler?.(data);
    };
    const tournamentWaitingStatusUpdateHandler = (data, roomIndex) => {
      console.log('Tournament waiting status update received:', data);
      tablesRefArray?.[roomIndex]?.current?.tournamentWaitingStatusUpdateHandler?.(data);
    };

    const reconnectTable = () => {
      tablesRefArray?.forEach(ref => ref?.current?.reconnect());
    };

    const handleJoinRoomResult = (data, roomIndex) => {
      if (!data?.success) {
        tablesRefArray?.[roomIndex]?.current?.joinRoomErrorHandler?.(data);
      }
    };

    const handleJoinTableResult = data => {
      if (data?.success) {
        // update joinedRooms => update isObserver
        user?.refreshPlayerInfo();
      }
    };

    const exitRoom = () => {
      console.log('exit page');
    };

    useEffect(() => {
      return () => {
        console.log('exit page');
      };
    }, []);

    const getTableInfo = index => {
      if (index >= tablesInfo.length) {
        return null;
      }
      return tablesInfo[index];
    };

    const assignRef = useCallback(index => {
      return tablesRefArray[index];
    }, []);

    const renderDepositPopupButtons = () => {
      // if (isTelegramMiniApp)
      if (false)
        return [
          {
            isBlue: false,
            buttonFunction: () => {
              setIsDepositPopupVisible(false);
            },
            buttonLabel: t('deposit_withdraw:cancel'),
          },
        ];
      return [
        {
          isBlue: true,
          buttonFunction: () => {
            system?.showModalSheet('depositWithdraw');
            setIsDepositPopupVisible(false);
          },
          buttonLabel: t('deposit_withdraw:deposit'),
        },
        {
          isBlue: false,
          buttonFunction: () => {
            setIsDepositPopupVisible(false);
          },
          buttonLabel: t('deposit_withdraw:cancel'),
        },
      ];
    };

    const renderTournamentEndedPopupButtons = () => {
      return [
        {
          isBlue: true,
          buttonFunction: () => {
            setIsTournamentEndedPopupVisible(false);
          },
          buttonLabel: t('common:confirm'),
        },
      ];
    };

    // Tournament cancel popup moved to lobby level
    // const renderTournamentCanceledPopupButtons = () => {
    //   return [
    //     {
    //       isBlue: true,
    //       buttonFunction: () => {
    //         setIsTournamentCanceledPopupVisible(false);
    //       },
    //       buttonLabel: t('common:confirm'),
    //     },
    //   ];
    // };

    return (
      <>
        {tablesInfo.map((table, tableIndex) => {
          return (
            <GameRoom
              key={table?.roomCode}
              ref={assignRef(tableIndex)}
              tableInfo={getTableInfo(tableIndex)}
              encryptionKey={encryptionKeys?.[tableIndex]}
              tableIndex={tableIndex}
              isTableActive={activeTableIndex === tableIndex}
              isGameOverlayShown={isGameOverlayShown}
              setIsWithdrawChipsBtnShown={setIsWithdrawChipsBtnShown}
              setIsDepositChipsBtnShown={setIsDepositChipsBtnShown}
              setIsEmojiBtnShown={setIsEmojiBtnShown}
              isGameRoomMuted={areTablesMuted}
              onAudioError={onAudioError}
            />
          );
        })}
        <CustomPopup
          // maskStyle={{background: 'rgba(74, 78, 86, 0.10)'}}
          centered
          width={250}
          open={isDepositPopupVisible}
          buttons={renderDepositPopupButtons()}
          closable={false}
          title={t('deposit_withdraw:notice')}
        >
          <div style={{textAlign: 'center'}}>
            {/* {isTelegramMiniApp ? t('deposit_withdraw:wait_for_next_lucky_draw') : t('HP_0003')} */}
            {t('HP_0003')}
          </div>
        </CustomPopup>
        <CustomPopup
          title={t('tournament:tournament_ended_msg')}
          open={isTournamentEndedPopupVisible}
          buttons={renderTournamentEndedPopupButtons()}
        >
          <div style={{textAlign: 'center'}}>{t('tournament:tournament_ended_msg')}</div>
        </CustomPopup>
        {/* Tournament cancel popup moved to lobby level */}
      </>
    );
  })
);

Tables.displayName = 'Tables';

export default Tables;
